import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, createBlock as _createBlock, withDirectives as _withDirectives, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["id", "aria-labelledby"]

import { onMounted, ref, watch } from "vue";
import { uniqueId } from "lodash";
import { useRouter, useRoute } from "vue-router";
import { wrap } from "@/util/math";

/** route info */
interface Tab {
  /** page-wide unique id of tab */
  id: string;
  /** tab button props */
  text?: string;
  icon?: string;
  description?: string;
  tooltip?: string;
}
type Tabs = Array<Tab>;

interface Props {
  /** two-way bound selected tab state */
  modelValue: string;
  /** list of tabs with info */
  tabs: Tabs;
  /** name of tab group */
  name: string;
  /** whether to sync active tab with url hash */
  url?: boolean;
  /** route name to navigate to on change */
  route?: string;
}

interface Emits {
  /** two-way bound selected tab state */
  (event: "update:modelValue", selected: string): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppTabs',
  props: {
    modelValue: null,
    tabs: null,
    name: null,
    url: { type: Boolean, default: true },
    route: { default: undefined }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }: { emit: ({
  /** two-way bound selected tab state */
  (event: "update:modelValue", selected: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { modelValue: string, tabs: Tabs, name: string, url: boolean, route: string }

const router = useRouter();
const route = useRoute();





/** unique id for instance of component */
const id = ref(uniqueId());

/** when user clicks on button */
async function onClick(id: string) {
  emit("update:modelValue", id);
}

/** when user presses key on button */
async function onKeydown(event: KeyboardEvent) {
  if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
    /** prevent page scroll */
    event.preventDefault();

    /** move selected tab */
    let index = props.tabs.findIndex((tab) => tab.id === props.modelValue);
    if (event.key === "ArrowLeft") index--;
    if (event.key === "ArrowRight") index++;
    if (event.key === "Home") index = 0;
    if (event.key === "End") index = props.tabs.length - 1;

    /** update selected, wrapping beyond -1 or options length */
    emit(
      "update:modelValue",
      props.tabs[wrap(index, 0, props.tabs.length - 1)].id
    );
  }
}

/** get appropriate tab from url hash */
function getHash() {
  /** set selected tab to id in hash */
  const hash = route?.hash?.slice(1) || "";
  /** if there is a tab with name equal to hash, return that one */
  if (props.tabs.find((tab) => tab.id === hash)) return hash;
  else return "";
}

/** when selected tab changes */
watch(
  () => props.modelValue,
  async () => {
    /** focus the selected tab */
    // const selector = `#tab-${id.value}-${props.modelValue}`;
    // const button = document?.querySelector(selector) as HTMLButtonElement;
    // button?.focus();

    /** update hash in url and nav if applicable */
    if (props.route) await router.replace({ name: props.route });
    else if (props.url) await router.push({ hash: "#" + props.modelValue });
  }
);

/** update state */
function updateState() {
  if (props.url && getHash()) emit("update:modelValue", getHash());
}

onMounted(updateState);
watch(() => route.hash, updateState);

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_AppFlex = _resolveComponent("AppFlex")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_AppFlex, {
      role: "tablist",
      "aria-label": __props.name
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.tabs, (tab, index) => {
          return _withDirectives((_openBlock(), _createBlock(_component_AppButton, {
            id: `tab-${id.value}-${tab.id}`,
            key: index,
            text: tab.text,
            icon: tab.icon,
            design: "circle",
            color: __props.modelValue === tab.id ? 'primary' : 'none',
            "aria-label": tab.text,
            "aria-selected": __props.modelValue === tab.id,
            "aria-controls": `panel-${id.value}-${tab.id}`,
            tabindex: __props.modelValue === tab.id ? 0 : undefined,
            role: "tab",
            onClick: ($event: any) => (onClick(tab.id)),
            onKeydown: onKeydown
          }, null, 8, ["id", "text", "icon", "color", "aria-label", "aria-selected", "aria-controls", "tabindex", "onClick"])), [
            [_directive_tooltip, tab.tooltip]
          ])
        }), 128))
      ]),
      _: 1
    }, 8, ["aria-label"]),
    _createElementVNode("div", {
      id: `panel-${id.value}-${__props.modelValue}`,
      "aria-labelledby": `tab-${id.value}-${__props.modelValue}`,
      role: "tabpanel",
      "aria-label": 'Tab content below',
      style: { display: 'contents' }
    }, null, 8, _hoisted_1)
  ], 64))
}
}

})